import React from 'react';
import { MessageCircle, MapPin, Mail, Instagram, Facebook, ShieldCheck, Lock, BadgeCheck, CreditCard } from 'lucide-react';
import { contactInfo, shippingInfo } from '../mock';
import { trackWhatsAppClick } from '../lib/analytics';

export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white pt-20 pb-10">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_13e5738d-7aa3-4072-b101-9672dafbd61a/artifacts/j65cu3zm_logo.jpg" 
              alt="Gafas La Tri" 
              className="h-12 w-auto mb-6"
             loading="lazy" decoding="async" />
            <p className="text-gray-400 leading-relaxed mb-6">
              La Tri en cada partido, sin sacrificar comodidad ni estilo.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/gafasdelatri" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="#" 
                className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Enlaces Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => document.getElementById('beneficios')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Beneficios
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('modelos')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Modelos
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('incluye')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Qué Incluye
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('comprar')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Comprar
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contacto</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors group"
                >
                  <MessageCircle className="group-hover:scale-110 transition-transform" size={20} />
                  <span>{contactInfo.whatsapp}</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400">
                <MapPin size={20} />
                <span>Punto de retiro: {shippingInfo.pickupLocation}</span>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="font-bold text-lg mb-6">Información</h4>
            <ul className="space-y-3 text-gray-400">
              <li>Envío a {shippingInfo.coverage}</li>
              <li>Producto 100% oficial</li>
              <li>Protección UV400 certificada</li>
              <li>Disponible en tiendas seleccionadas</li>
            </ul>
          </div>
        </div>

        {/* Security / Trust Strip */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-10" data-testid="footer-trust-strip">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div className="flex items-center gap-3">
              <div className="bg-green-500/15 p-2.5 rounded-lg flex-shrink-0">
                <ShieldCheck className="text-green-400" size={22} strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-sm">Compra 100% segura</div>
                <div className="text-xs text-gray-400">Tienda verificada</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-blue-500/15 p-2.5 rounded-lg flex-shrink-0">
                <Lock className="text-blue-400" size={22} strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-sm">Pago cifrado SSL</div>
                <div className="text-xs text-gray-400">Conexión 256-bit</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-yellow-500/15 p-2.5 rounded-lg flex-shrink-0">
                <CreditCard className="text-yellow-400" size={22} strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-sm">Procesado por Payphone</div>
                <div className="text-xs text-gray-400">No guardamos tu tarjeta</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-purple-500/15 p-2.5 rounded-lg flex-shrink-0">
                <BadgeCheck className="text-purple-400" size={22} strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-sm">Producto oficial FEF</div>
                <div className="text-xs text-gray-400">Con licencia</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © 2025 Gafas La Tri. Todos los derechos reservados.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Política de Privacidad</a>
              <a href="#" className="hover:text-white transition-colors">Términos y Condiciones</a>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href={contactInfo.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsAppClick({ location: 'footer-floating', source: sessionStorage.getItem('campaign_source') || 'direct' })}
        className="fixed bottom-8 right-8 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 hover:scale-110 transition-all z-50 animate-bounce"
        aria-label="Contactar por WhatsApp"
        data-testid="footer-whatsapp-floating"
      >
        <MessageCircle size={28} />
      </a>
    </footer>
  );
};

export default Footer;
